import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { addressAPI } from '../lib/api';

const AddressAutocomplete = ({ value, onChange, onSelect, placeholder = "Rechercher une adresse...", label, required = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  const search = useCallback(async (query) => {
    if (!query || query.length < 4) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await addressAPI.search(query);
      setSuggestions(res.data.results || []);
      setOpen(true);
    } catch { setSuggestions([]); }
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (item) => {
    onSelect({
      address: item.street,
      city: item.city,
      postal_code: item.postal_code,
      full_label: item.label,
    });
    onChange(item.label);
    setOpen(false);
    setSuggestions([]);
  };

  useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="address-autocomplete">
      {label && <label className="block text-xs font-semibold text-[#033871] mb-1.5">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8D4E3]" />
        <input
          value={value}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="w-full h-10 pl-9 pr-9 rounded-xl border border-[#D6E8F0] text-sm text-[#033871] placeholder-[#B8D4E3] focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none"
          data-testid="address-input"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0C63BF] animate-spin" />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-[#D6E8F0] shadow-lg overflow-hidden max-h-48 overflow-y-auto" data-testid="address-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => handleSelect(s)}
              className="w-full text-left px-3 py-2.5 hover:bg-[#F0F7FA] border-b border-[#F0F7FA] last:border-b-0 transition-colors"
              data-testid={`address-suggestion-${i}`}
            >
              <p className="text-sm font-medium text-[#033871]">{s.street}</p>
              <p className="text-[11px] text-[#5A8BA8]">{s.postal_code} {s.city}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
